import { inject, LOCALE_ID, Pipe, PipeTransform } from "@angular/core";
import { FlameDetectionRange } from "@domain/product/flame-detection-range";
import { ProductCountryData } from "@domain/product/product-country-data";
import { Expose, Transform, Type } from "class-transformer";

export class Product {
  @Expose({ name: "localizedName" })
  private _localizedName!: Record<string, string>;

  @Expose({ name: "type" })
  private readonly _type: FgdsProductType;

  @Expose({ name: "fullType" })
  private readonly _fullType: FullProductType;

  @Type(() => Relationship)
  @Expose({ name: "relationships" })
  private readonly _relationships: Relationship[] = [];

  @Expose({ name: "properties" })
  private readonly _properties: ProductProperty[] = [];

  @Transform(
    ({ obj }) => {
      return Array.isArray(obj.countryData) ? obj.countryData : [];
    },
    { toClassOnly: true },
  )
  @Expose({ name: "countryData" })
  public countryData: ProductCountryData[] = [];

  @Expose()
  @Type(() => FlameDetectionRange)
  public flameDetectionData?: FlameDetectionRange[];

  @Expose()
  @Transform(({ value }) => (Array.isArray(value) ? value : []), { toClassOnly: true })
  public parentFamilies: string[] = [];

  @Expose()
  public subFamily?: string;

  constructor(
    public id: string,
    public name: string,
    type: FgdsProductType,
    fullType: FullProductType,
    public isModular: boolean,
    public isAvailable: boolean,
    public cantoImageId: string,
    public cantoIfuId: string,
    public cantoProductInfoId: string,
    relationships: Relationship[],
    properties: ProductProperty[],
    countryData: ProductCountryData[],
  ) {
    this._type = type;
    this._fullType = fullType;
    this._relationships = relationships;
    this._properties = properties;
    this.countryData = countryData;
  }

  get type(): FgdsProductType {
    return this._type;
  }

  get fullType(): FullProductType {
    return this._fullType;
  }

  get relationships(): readonly Relationship[] {
    return this._relationships;
  }

  get properties(): readonly ProductProperty[] {
    return this._properties;
  }

  isAvailableInCountry(country: string): boolean {
    return this.countryData.some((countryData) => countryData.countryCode == country);
  }

  getLocalizedName(locale: string, fallbackLocal: string = "en-US"): string {
    if (!this._localizedName) return "";
    if (this._localizedName[locale]) return this._localizedName[locale];
    if (!!fallbackLocal && this._localizedName[fallbackLocal]) return this._localizedName[fallbackLocal];
    return "";
  }

  addCountryData(country: string, countryData: ProductCountryData) {
    if (!this.isAvailableInCountry(country)) {
      this.countryData.push(countryData);
    }
  }

  /**
   * ZREF prices are global. After merging country data from multiple API responses,
   * some countries (e.g. TEST) may not have ZREF prices in their API response.
   * This method finds the ZREF price from any countryData entry and adds it to all
   * entries that don't have one yet.
   */
  distributeZrefPrices() {
    const zrefPrice = this.countryData
      .flatMap((cd) => cd.prices ?? [])
      .find((p) => p.type.trim().toUpperCase().endsWith(Product.ZREF_PRICE_SUFFIX));
    if (!zrefPrice) return;

    for (const cd of this.countryData) {
      const hasZref = (cd.prices ?? []).some((p) => p.type.trim().toUpperCase().endsWith(Product.ZREF_PRICE_SUFFIX));
      if (!hasZref) {
        if (!cd.prices) cd.prices = [];
        cd.prices.push(zrefPrice);
      }
    }
  }

  private static readonly ZREF_PRICE_SUFFIX = "ZREF";
  private static readonly ZREF_ONLY_COUNTRIES = ["TEST", "MX", "BR"];

  getPriceDataByCountry(country: string) {
    const cd = this.countryData.find((countryData) => countryData.countryCode == country);
    if (!cd) return undefined;
    if (Product.ZREF_ONLY_COUNTRIES.includes(country.trim().toUpperCase())) return undefined;
    return (cd.prices ?? []).find((p) => !p.type.trim().toUpperCase().endsWith(Product.ZREF_PRICE_SUFFIX));
  }

  getZrefPriceDataByCountry(country: string) {
    const cd = this.countryData.find((countryData) => countryData.countryCode === country);
    return (cd?.prices ?? []).find((p) => p.type.trim().toUpperCase().endsWith(Product.ZREF_PRICE_SUFFIX));
  }

  addLocalizedName(language: string, name: string) {
    if (!this._localizedName) {
      this._localizedName = {};
    }
    if (!this._localizedName[language]) {
      this._localizedName[language] = name;
    }
  }

  getTextPropertyByKey(key: string): ProductPropertyText {
    return this.getPropertyByKey(key) as ProductPropertyText;
  }

  getBooleanPropertyByKey(key: string): ProductPropertyBoolean {
    return this.getPropertyByKey(key) as ProductPropertyBoolean;
  }

  getDimensionsPropertyByKey(key: string): ProductPropertyDimension {
    return this.getPropertyByKey(key) as ProductPropertyDimension;
  }

  private getPropertyByKey(key: string): ProductProperty | undefined {
    if (this.properties.length == 0) return undefined;
    return this.properties.find((property) => property.key.startsWith(key));
  }

  static sort(products: Product[]): Product[] {
    return products.sort((a, b) => {
      if (a.name < b.name) {
        return -1;
      }
      if (a.name > b.name) {
        return 1;
      }
      return 0;
    });
  }
}

export class Relationship {
  constructor(
    public productId: string,
    public relationshipType: RelationshipType,
    public matchType: MatchType,
    public relationshipSubType?: RelationshipSubType,
  ) {}
}

export enum RelationshipType {
  EXTRAGUIDE = "EXTRAGUIDE",
  EXTRA = "EXTRA",
  EXTRAREQ = "EXTRAREQ",
  SENSOR = "SENSOR",
  MODULE = "MODULE",
}

export enum MatchType {
  DIRECT = "DIRECT",
  SENSORTYPE = "SENSORTYPE",
}

export enum RelationshipSubType {
  INPUT = "INPUT",
  SLOT_COVER = "SLOTCOVER",
  RELAY = "RELAY",
  GATEWAY = "GATEWAY",
}

export function isRequiredAttachment(relationship: Relationship): boolean {
  return (
    relationship.matchType === MatchType.DIRECT &&
    (relationship.relationshipType === RelationshipType.EXTRAREQ || relationship.relationshipType === RelationshipType.EXTRAGUIDE)
  );
}

export enum FgdsProductType {
  TRANSMITTER = "TRANSMITTER",
  ATTACHMENT = "ATTACHMENT",
  SENSOR = "SENSOR",
  GASWARNINGCENTER = "GASWARNINGCENTER",
  ALARMDEVICE = "ALARMDEVICE",
  MISCDEVICE = "MISCDEVICE",
  PLASTICSIGN = "PLASTICSIGN",
  SIGNALELEMENT = "SIGNALELEMENT",
  SERVICE = "SERVICE",
  FLAME_DETECTOR = "FLAME_DETECTOR",
}

export const FGDS_SEARCHABLE_PRODUCT_TYPES: FgdsProductType[] = [
  FgdsProductType.ALARMDEVICE,
  FgdsProductType.GASWARNINGCENTER,
  FgdsProductType.TRANSMITTER,
  FgdsProductType.FLAME_DETECTOR,
  FgdsProductType.PLASTICSIGN,
  FgdsProductType.SIGNALELEMENT,
];

export enum FullProductType {
  ALARMDEVICE_A24 = "a-24", //Alarmmittel, non-ex, 24 V
  ALARMDEVICE_A230 = "a-230", //Alarmmittel, non-ex, 230 V
  ALARMDEVICE_A24_MOD = "a-24-mod", //Alarmmittel, non-ex, 24 V, modular
  ALARMDEVICE_A230_MOD = "a-230-mod", //Alarmmittel, non-ex, 230 V, modular
  ALARMDEVICE_A_EX_24 = "a-ex-24", //Alarmmittel, ex geschützt, 24 V
  ALARMDEVICE_A_EX_230 = "a-ex-230", //Alarmmittel, ex geschützt, 230 V
  FLAME_DETECTOR_T_FLAME_K = "t-flame-k", // Brandmelder
  TRANSMITTER_EC_M = "t-ec-m", // Transmitter (Sensor nicht verbaut?)
  TRANSMITTER_IR_M = "t-ir-m", // Transmitter PIR (Sensor nicht verbaut?)
  TRANSMITTER_MEC_M = "t-mec-m", // Transmitter (Sensor nicht verbaut?)
  TRANSMITTER_CAT_M = "t-cat-m", // Transmitter (Sensor nicht verbaut?)
  TRANSMITTER_CAT_K = "t-cat-k", // Transmitter (Sensor verbaut?)
  TRANSMITTER_IR_K_BUS = "t-ir-k-bus", // Transmitter PIR (Sensor verbaut?)
  TRANSMITTER_IR_K = "t-ir-k", // Transmitter PIR (Sensor verbaut?)
  TRANSMITTER_EC_K_BUS = "t-ec-k-bus", // Transmitter (Sensor verbaut?)
  ATTACHMENT_OPTIONAL = "z-o", // Zubehör Optional
  ATTACHMENT_REQ = "z-n", // Zubehör Notwendig
  ATTACHMENT_INSTRUCTIONS = "z-ga", // Zubehör Gebrauchsanweisung
  ATTACHMENT_MODULE = "z-m", // Zubehör Modul für Gaswarnzentrale?
  ATTACHMENT_OPTIONAL_SLOTCOVER = "z-o-slotcover", // Slotcover
  GASWARNINGCENTER = "gwz",
  GASWARNINGCENTER_M = "gwz-m", // Gaswarnzentrale (Modular?)
  GASWARNINGCENTER_K = "gwz-k", // Gaswarnzentrale (Nicht modular?)
  GASWARNINGCENTER_K_VG = "gwz-k-vg", // Gaswarnzentrale (Nicht modular?)
  GASWARNINGCENTER_BASE = "gwz-base", // Gaswarnzentrale Basiseinheit
  REGARD_3000_DB = "z-n-gwz-db", // Gaswarnzentrale Regard Deckeleinheit
  REGARD_3000_INPUT_ANALOG = "z-o-gwz-input-analog", // Gaswarnzentrale Regard Input Modul
  REGARD_3000_RELAIS = "z-o-gwz-relais", // Gaswarnzentrale Regard Relais Modul
  REGARD_3000_GATEWAY = "z-o-gwz-gateway", // Gaswarnzentrale Regard Gateway Modul
  REGARD_3000_DS = "z-o-gwz-ds", // Gaswarnzentrale Regard Docking Station
  GASWARNINGCENTER_DS_8_SLOT = "gwz-ds-8-slot", // Gaswarnzentrale Regard 7000 Docking Station 8-Slot
  GASWARNINGCENTER_DS_4_SLOT = "gwz-ds-4-slot", // Gaswarnzentrale Regard 7000 Docking Station 4-Slot
  GASWARNINGCENTER_INPUT_ANALOG = "gwz-input-analog", // Gaswarnzentrale Regard 7000 Input Module Analog
  GASWARNINGCENTER_INPUT_DIGITAL = "gwz-input-digital", // Gaswarnzentrale Regard 7000 Input Module Digital
  GASWARNINGCENTER_INPUT_BUS = "gwz-input-bus", // Gaswarnzentrale Regard 7000 Input Module Bus
  GASWARNINGCENTER_RELAYS_24V = "gwz-relais-24v", // Gaswarnzentrale Regard 7000 Relay Module 24V
  GASWARNINGCENTER_RELAYS_240V = "gwz-relais-240v", // Gaswarnzentrale Regard 7000 Relay Module 240V
  GASWARNINGCENTER_GATEWAY = "gwz-gateway", // Gaswarnzentrale Regard 7000 Gateway Module
  GASWARNINGCENTER_TB = "gwz-tb", // Gaswarnzentrale Regard 7000 Terminal Block
  GASWARNINGCENTER_TB_BUS_INPUT = "gwz-tb-bus-input", // Gaswarnzentrale Regard 7000 Terminal Block Bus
  GASWARNINGCENTER_TB_DIGITAL_INPUT = "gwz-tb-digital-input", // Gaswarnzentrale Regard 7000 Terminal Block Digital
  GASWARNINGCENTER_TB_RELAYS_240V = "gwz-tb-relais-240v", // Gaswarnzentrale Regard 7000 Terminal Block Relais 240V
  GASWARNINGCENTER_TB_ANALOG_INPUT_RELAYS_24V = "gwz-tb-analog-input-relais-24v", // Gaswarnzentrale Regard 7000 Terminal Block Analog Input Relais 24V
  GASWARNINGCENTER_DB = "gwz-db", // Gaswarnzentrale Regard 7000 Dashboard
  ALARMDEVICE = "a", // Alarmmittel
  PLASTICSIGN = "sz-ks", // Kunststoffschilder
  SIGNALELEMENT = "sz-lt", // Leuchttransparente
  MISCDEVICE = "sz", // Misc
  SERVICE = "dl", // Dienstleistung
  SENSOR_EC = "s-ec", // Sensor
  SENSOR_MEC = "s-mec", // Sensor
  SENSOR_IR = "s-ir", // Sensor
  SENSOR_CAT = "s-cat", // Sensor
  //TODO add flame detectors
}

export enum ProductPropertyType {
  BOOLEAN = "BOOLEAN",
  TEXT = "TEXT",
  NUMBER = "NUMBER",
  NUMBER_RANGE = "NUMBER_RANGE",
  WEIGHT_IN_KG = "WEIGHT_IN_KG",
  TEMPERATUR_RANGE_IN_CELSIUS = "TEMPERATUR_RANGE_IN_CELSIUS",
  DIMENSIONS_IN_METER = "DIMENSIONS_IN_METER",
}

export class ProductPropertyTemplate {
  constructor(
    public key: string,
    public type: ProductPropertyType,
    public name: string,
    public info?: string,
    public description?: string,
  ) {}

  createInstance(value: string): ProductProperty {
    value = value.trim();

    switch (this.type) {
      case ProductPropertyType.BOOLEAN:
        return ProductPropertyBoolean.create(this, value);
      case ProductPropertyType.TEXT:
        return ProductPropertyText.create(this, value);
      case ProductPropertyType.NUMBER:
      case ProductPropertyType.WEIGHT_IN_KG:
      case ProductPropertyType.TEMPERATUR_RANGE_IN_CELSIUS:
        return ProductPropertyNumber.create(this, value);
      case ProductPropertyType.NUMBER_RANGE:
        return ProductPropertyNumberRange.create(this, value);
      case ProductPropertyType.DIMENSIONS_IN_METER:
        return ProductPropertyDimension.create(this, value);
      default:
        throw new Error("Unknown property dataType " + this.type);
    }
  }
}

export interface ProductProperty {
  readonly key: string;
  readonly type: ProductPropertyType;
  readonly name: string;
  readonly info?: string;
  readonly decription?: string;
}

export class ProductPropertyText implements ProductProperty {
  public readonly type = ProductPropertyType.TEXT;

  constructor(
    public readonly key: string,
    public readonly name: string,
    public readonly value: string,
    public readonly info?: string,
    public readonly description?: string,
  ) {}

  static create(template: ProductPropertyTemplate, value: string): ProductPropertyText {
    return new ProductPropertyText(template.key, template.name, value, template.info, template.description);
  }
}

export class ProductPropertyNumber implements ProductProperty {
  constructor(
    public readonly key: string,
    public readonly type: ProductPropertyType,
    public readonly name: string,
    public readonly value: number,
    public readonly info?: string,
    public readonly description?: string,
  ) {}

  static create(template: ProductPropertyTemplate, value: string): ProductPropertyNumber {
    return new ProductPropertyNumber(template.key, template.type, template.name, +value, template.info, template.description);
  }
}

export class ProductPropertyBoolean implements ProductProperty {
  public readonly type = ProductPropertyType.BOOLEAN;

  constructor(
    public readonly key: string,
    public readonly name: string,
    public readonly value: boolean,
    public readonly info?: string,
    public readonly description?: string,
  ) {}

  static create(template: ProductPropertyTemplate, value: string): ProductPropertyBoolean {
    return new ProductPropertyBoolean(
      template.key,
      template.name,
      value.indexOf("true") > -1,
      template.info,
      template.description,
    );
  }
}

export class ProductPropertyNumberRange implements ProductProperty {
  constructor(
    public readonly key: string,
    public readonly type: ProductPropertyType,
    public readonly name: string,
    public readonly startValue: number,
    public readonly endValue: number,
    public readonly info?: string,
    public readonly description?: string,
  ) {}

  static create(template: ProductPropertyTemplate, value: string): ProductPropertyNumberRange {
    const range = value.split(" ");
    return new ProductPropertyNumberRange(
      template.key,
      template.type,
      template.name,
      +range[0],
      +range[1],
      template.info,
      template.description,
    );
  }
}

export class ProductPropertyDimension implements ProductProperty {
  constructor(
    public readonly key: string,
    public readonly type: ProductPropertyType,
    public readonly name: string,
    public readonly height: number,
    public readonly width: number,
    public readonly depth: number,
    public readonly info?: string,
    public readonly description?: string,
  ) {}

  static create(template: ProductPropertyTemplate, value: string): ProductPropertyDimension {
    const range = value.split(" ");
    return new ProductPropertyDimension(
      template.key,
      template.type,
      template.name,
      +range[0],
      +range[1],
      +range[2],
      template.info,
      template.description,
    );
  }
}

@Pipe({
  standalone: true,
  name: "localizeProductName",
})
export class LocalizeProductNamePipe implements PipeTransform {
  private localeId = inject(LOCALE_ID);

  transform(product: Product): string {
    return product.getLocalizedName(this.localeId);
  }
}

@Pipe({
  standalone: true,
  name: "displayName",
})
export class DisplayNamePipe implements PipeTransform {
  transform(value: string, limit: number = 100): string {
    if (!value) return "";
    return value.length > limit ? value.substring(0, limit) + "..." : value;
  }
}

@Pipe({
  standalone: true,
  name: "localizeProductType",
})
export class LocalizeProductTypePipe implements PipeTransform {
  private readonly productTypeToLocalizedStringMap = {
    [FgdsProductType.TRANSMITTER]: $localize`:@@productType.transmitters:Transmitter`,
    [FgdsProductType.FLAME_DETECTOR]: $localize`:@@productType.flameDetectors:Brandmelder`,
    [FgdsProductType.ATTACHMENT]: $localize`:@@productType.attachments:Zubehör`,
    [FgdsProductType.SENSOR]: $localize`:@@productType.sensors:Sensoren`,
    [FgdsProductType.GASWARNINGCENTER]: $localize`:@@productType.gasWarningCenters:Gaswarnzentralen`,
    [FgdsProductType.ALARMDEVICE]: $localize`:@@productType.alarmDevices:Alarmmittel`,
    [FgdsProductType.MISCDEVICE]: $localize`:@@productType.miscDevices:Verschiedene Geräte`,
    [FgdsProductType.PLASTICSIGN]: $localize`:@@productType.plastigSigns:Kunststoffschilder`,
    [FgdsProductType.SIGNALELEMENT]: $localize`:@@productType.signalElements:Leuchttransparente`,
    [FgdsProductType.SERVICE]: $localize`:@@productType.services:Dienstleistungen`,
  };

  transform(value: FgdsProductType): string {
    return this.productTypeToLocalizedStringMap[value];
  }
}
